import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiConfiguration } from '@api/api-configuration';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

export interface ConsentResponse {
  surveyId: number;
  tid: string;
  token: string;
  url: string;
  survey: SurveyRow;
}

export interface SurveyRow {
  surveyKey: string | null;
  surveyType: string | null;
  status: string | null;
  sentDate: string | null;
  completeDate: string | null;
}

export interface MemberRegistrationResponse {
  status: string;
  correlationId: string;
  stages: {
    program: string;
    coach: string;
    member: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SurveysService {
  constructor(
    private http: HttpClient,
    private apiConfiguration: ApiConfiguration
  ) {}

  private enabled$?: Observable<boolean>;

  isEnabled(): Observable<boolean> {
    if (this.enabled$) {
      return this.enabled$;
    }

    const rootUrl = this.apiConfiguration.rootUrl.replace(/\/$/, '');

    this.enabled$ = this.http.get<boolean>(
      `${rootUrl}/api/Case/surveys/enabled`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    ).pipe(
      map(enabled => enabled === true),
      catchError(() => of(false)),
      shareReplay(1)
    );

    return this.enabled$;
  }

  getSurveys(parentCaseKey: number): Observable<SurveyRow[]> {
    const rootUrl = this.apiConfiguration.rootUrl.replace(/\/$/, '');

    return this.http.get<SurveyRow[]>(
      `${rootUrl}/api/Case/surveys/${parentCaseKey}`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
  }

  getPortalConsentStatus(parentCaseKey: number): Observable<boolean> {
    const rootUrl = this.apiConfiguration.rootUrl.replace(/\/$/, '');

    return this.http.get<boolean>(
      `${rootUrl}/api/Case/surveys/portal-consent-status/${parentCaseKey}`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
  }

  registerMember(
    parentCaseKey: number,
    caseFloInstanceId: string
  ): Observable<MemberRegistrationResponse> {
    const rootUrl = this.apiConfiguration.rootUrl.replace(/\/$/, '');

    return this.http.post<MemberRegistrationResponse>(
      `${rootUrl}/api/Case/surveys/member-registration/${parentCaseKey}`,
      { caseFloInstanceId },
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
  }

  requestConsent(
    parentCaseKey: number,
    userEmail: string
  ): Observable<ConsentResponse> {
    const rootUrl = this.apiConfiguration.rootUrl.replace(/\/$/, '');

    return this.http.post<ConsentResponse>(
      `${rootUrl}/api/Case/surveys/consent/${parentCaseKey}`,
      {
        userEmail
      },
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
  }
}